import { forwardRef, RefObject } from 'react';

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Portal,
  Text,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  ArrowRightOutline,
  DropdownInput,
  DropdownOption,
  EmptyState,
  shadowStyles,
  textStyles,
} from '@aslanpay/portal-common-lib';

import { useImportEmployeesStore } from 'app/store';

import { importEmployeesCompanyFormSchema } from 'app/utils/schemas';

import { getHomePath } from 'app/utils/routes';

import { SelectCompanyFormFields, SelectCompanyStepProps } from '../types';

const SelectCompany = forwardRef<HTMLElement, SelectCompanyStepProps>(
  ({ id, onStepAction, companies }, ref) => {
    const { importEmployeesData, setImportEmployeesData } = useImportEmployeesStore.getState();

    const {
      control,
      handleSubmit,
      formState: { errors, isValid },
    } = useForm<SelectCompanyFormFields>({
      mode: 'onBlur',
      defaultValues: { companyId: importEmployeesData.companyId },
      resolver: yupResolver(importEmployeesCompanyFormSchema),
    });

    const handleNextStep = ({ companyId }: SelectCompanyFormFields) => {
      const companyName = companies.find((company) => company.id === companyId)?.name || '';

      setImportEmployeesData({
        ...importEmployeesData,
        companyId,
        companyName,
      });

      onStepAction();
    };

    const companyOptions: DropdownOption[] =
      companies.map((company) => ({
        value: company.id,
        text: company.name,
      })) || [];

    return companies.length === 0 ? (
      <EmptyState
        full={false}
        text="You need to have at least one company in order to proceed."
        link={getHomePath()}
        linkText="Go to companies"
      />
    ) : (
      <Flex
        flexDir="column"
        maxW="572px"
        alignSelf="center"
        gap="30px"
        borderRadius="10px"
        boxShadow={shadowStyles.dropdownMenu}
      >
        <form id={id} onSubmit={handleSubmit(handleNextStep)} noValidate>
          <FormControl isInvalid={!!errors?.companyId?.message} isRequired>
            <HStack
              minH="65px"
              px="40px"
              py="20px"
              borderBottom="1px solid"
              borderColor="brand.lightGray1"
              alignItems="center"
              justify="space-between"
            >
              <FormLabel as="h5" sx={{ ...textStyles.h5Bold }} margin="0">
                Select the company
              </FormLabel>
            </HStack>
            <Flex flexDir="column" px="40px" py="20px" gap="20px">
              <Text color="brand.mutedPurple" textStyle="smallMedium">
                Please select the company you want to upload your employees&apos; data to.
              </Text>

              <Flex flexDir="column">
                <Controller
                  control={control}
                  name="companyId"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DropdownInput
                      placeholder="Select the company"
                      defaultValue={companyOptions.find((company) => company.value === value)}
                      options={companyOptions}
                      onChange={onChange}
                      onBlur={onBlur}
                      isSearchable
                      searchPlaceholder="Search companies..."
                    />
                  )}
                />
                <FormErrorMessage>{errors?.companyId?.message}</FormErrorMessage>
              </Flex>
              <Text alignSelf="flex-start" color="system.error" textStyle="extraSmallMedium">
                * mandatory
              </Text>
            </Flex>
          </FormControl>
          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid}
              rightIcon={<ArrowRightOutline boxSize="20px" />}
            >
              Next
            </Button>
          </Portal>
        </form>
      </Flex>
    );
  }
);

SelectCompany.displayName = 'SelectCompany';

export default SelectCompany;
